import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons";
import { toggleMentorBookmark } from "../../stores/bookmarks/mentorBookmarksSlice";
import styles from "./CardStyles.module.css";
import cx from "classnames";
import type { Mentor } from "../../shared/mentorTypes";
import { useAppDispatch, useAppSelector } from "../../app/store";
import {
  bookmarkEventBuilder,
  mentorCardClickEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { useNavigate } from "react-router-dom";
import useTenant from "../../tenancy/useTenantPath";

interface CardProps {
  mentor: Mentor;
  isBookmarked: boolean;
  dataCy?: string;
}

const Card = ({ mentor, isBookmarked, dataCy }: CardProps) => {
  const { _id, name, title, thumbnail } = mentor;
  const dispatch = useAppDispatch();

  const { userId, sessionId } = useAppSelector((state) => state.session);

  const navigate = useNavigate();
  const tenant = useTenant();

  const handleBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!name) {
      console.warn("⚠️ Bookmark skipped due to missing bookmark ID.");
      return;
    }

    if (!userId || !sessionId) {
      console.warn("⚠️ Bookmark skipped due to missing user or session ID.");
      return;
    }

    const eventData = bookmarkEventBuilder(
      !isBookmarked,
      userId,
      sessionId,
      "Mentor",
      name,
      {},
    );
    void dispatch(sendEvent(eventData));

    dispatch(
      toggleMentorBookmark({
        userId,
        mentorId: _id,
      }),
    );
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target;

    if (!(target instanceof Element)) return;

    if (!target.closest(".card-bookmark-button")) {
      navigate(tenant.basePath + "/mentorpal?mentorId=" + mentor._id);
    }

    if (!userId || !sessionId) {
      return;
    }

    const eventData = mentorCardClickEventBuilder(
      userId,
      sessionId,
      mentor,
      {},
    );
    void dispatch(sendEvent(eventData));
  };

  return (
    <div className={styles.card} onClick={handleCardClick} data-cy={dataCy}>
      <div className={styles.cardContent}>
        <h3 className={styles.cardTitle}>{name}</h3>
        <div className={styles.cardLink}>
          <img src={thumbnail} alt={title} className={styles.cardThumbnail} />
        </div>
        <h3 className={styles.cardSubtitle}>{title}</h3>
      </div>

      <button
        data-cy="bookmark-card"
        onClick={handleBookmark}
        className={styles.cardBookmarkButton}
        aria-label={isBookmarked ? "Remove Bookmark" : "Add Bookmark"}
      >
        <FontAwesomeIcon
          icon={isBookmarked ? faBookmarkSolid : faBookmarkSolid}
          className={cx(
            styles.cardBookmarkIcon,
            isBookmarked ? styles.blue : styles.white,
          )}
        />
      </button>
    </div>
  );
};

export default Card;
